import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiResponse, PageableDto } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import { mapMutationMessage } from '../../utils/map-pageable';

/** Shape API de un documento del customer. */
export interface CustomerDocumentDto {
  id: string;
  customerId?: string;
  typeDocumentId?: string;
  typeDocumentPrefix?: string;
  typeDocumentCode?: string;
  typeDocumentName?: string;
  name: string;
  fileUrl?: string;
  originalName?: string;
  contentType?: string;
  fileSize?: number;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Metadatos del multipart (sin el File). */
export type CustomerDocumentWritePayload = {
  name: string;
  typeDocumentId: string;
};

export type CustomerDocumentListParams = {
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  status?: string;
  search?: string;
};

/**
 * Documentos del cliente.
 *   GET    /api/customers/{customerId}/documents
 *   POST   /api/customers/{customerId}/documents  (form-data: name, typeDocumentId, file)
 *   PUT    /api/customers/{customerId}/documents/{id}  (form-data: name, typeDocumentId, file?)
 *   PATCH  /api/customers/{customerId}/documents/{id}/status
 *   DELETE /api/customers/{customerId}/documents/{id}
 *
 * Store (Postman): multipart con `name`, `typeDocumentId`, `file`.
 * Respuesta create: `{ success, message, timestamp }` (sin `data`).
 * Update: misma forma de body; `file` opcional si no se reemplaza el archivo.
 */
@Injectable({ providedIn: 'root' })
export class CustomerDocumentService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private resource(customerId: string): string {
    return `${this.apiConfig.apiUrl}/customers/${customerId}/documents`;
  }

  list(
    customerId: string,
    params: CustomerDocumentListParams = {},
  ): Observable<CustomerDocumentDto[]> {
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 0))
      .set('size', String(params.size ?? 100))
      .set('sort', params.sort ?? 'createdAt')
      .set('direction', params.direction ?? 'desc');

    if (params.status?.trim()) {
      httpParams = httpParams.set('status', params.status.trim());
    }
    if (params.search?.trim()) {
      httpParams = httpParams.set('search', params.search.trim());
    }

    return this.http
      .get<
        ApiResponse<CustomerDocumentDto[] | PageableDto<CustomerDocumentDto>>
      >(this.resource(customerId), { params: httpParams })
      .pipe(map((res) => this.unwrapList(res.data)));
  }

  /** POST form-data: name, typeDocumentId, file (obligatorio). */
  create(
    customerId: string,
    payload: CustomerDocumentWritePayload,
    file: File,
  ): Observable<{ message: string }> {
    return this.http
      .post<{ success?: boolean; message?: string }>(
        this.resource(customerId),
        this.toFormData(payload, file),
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Documento creado'),
        ),
      );
  }

  /**
   * PUT form-data: name, typeDocumentId y `file` solo si se reemplaza.
   * Misma clave/forma que el store.
   */
  update(
    customerId: string,
    documentId: string,
    payload: CustomerDocumentWritePayload,
    file?: File | null,
  ): Observable<{ message: string }> {
    return this.http
      .put<{ success?: boolean; message?: string }>(
        `${this.resource(customerId)}/${documentId}`,
        this.toFormData(payload, file),
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Documento actualizado'),
        ),
      );
  }

  updateStatus(
    customerId: string,
    documentId: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    const apiStatus = status === 'inactive' ? 'INACTIVE' : 'ACTIVE';
    return this.http
      .patch<{ success?: boolean; message?: string }>(
        `${this.resource(customerId)}/${documentId}/status`,
        { status: apiStatus },
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Estatus actualizado exitosamente'),
        ),
      );
  }

  delete(
    customerId: string,
    documentId: string,
  ): Observable<{ message: string }> {
    return this.http
      .delete<{ success?: boolean; message?: string }>(
        `${this.resource(customerId)}/${documentId}`,
      )
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Documento eliminado exitosamente'),
        ),
      );
  }

  /** form-data alineado a Postman: name, typeDocumentId, file. */
  private toFormData(
    payload: CustomerDocumentWritePayload,
    file?: File | null,
  ): FormData {
    const formData = new FormData();
    formData.append('name', payload.name);
    formData.append('typeDocumentId', payload.typeDocumentId);
    if (file) {
      formData.append('file', file);
    }
    return formData;
  }

  private unwrapList(
    data:
      | CustomerDocumentDto[]
      | PageableDto<CustomerDocumentDto>
      | null
      | undefined,
  ): CustomerDocumentDto[] {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.content)) return data.content;
    return [];
  }
}
